import React, { useCallback, useEffect, useMemo, useState } from "react";
import API from "../services/api";
import { Calendar, ChevronLeft, ChevronRight, RefreshCw, AlertTriangle } from "lucide-react";
import { errMsg, nzToday, addDay, prettyDate } from "./runsheets/util";
import { breakSpans } from "./timesheets/util";

/**
 * Daily Hours - the day-by-day record of what every driver and offsider
 * worked, and the breaks they took.
 *
 * This replaced the old Roster planner, which drew a seven-day grid that was
 * always empty (its backend was a stub returning []). The operator asked for
 * the tab to become "a record of each day the drivers' and offsiders' number
 * of hours worked and breaks" - so it reads the same shifts the Timesheets
 * tab pays from, one day at a time, and changes nothing. Corrections and
 * manual entry live on Timesheets; this is the page you open to answer
 * "who worked on Tuesday, and for how long".
 *
 * Breaks are shown, never deducted - they are paid here (operator decision).
 */
const ROLE_LABEL = { driver: "Driver", offsider: "Offsider" };

const fmtHours = (h) => (h == null ? "—" : Number(h).toFixed(2));
const fmtBreak = (m) => (!m ? "—" : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60 ? `${m % 60}m` : ""}`.trim() : `${m} min`);

// "12:00–12:30 · 30 min" - the recorded times and the total. A day that only
// has a minutes total (keyed in before breaks had times, or a Log Ninja
// import) falls back to the total on its own.
const fmtBreaks = (s) => {
  const spans = breakSpans(s);
  if (!spans.length) return fmtBreak(s.break_minutes);
  return s.break_minutes ? `${spans.join(", ")} · ${fmtBreak(s.break_minutes)}` : spans.join(", ");
};

const TIER_CLASS = {
  over_limit: "bg-red-500/10 text-red-300 border-red-500/30",
  check: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  info: "bg-violet-500/10 text-violet-300 border-violet-500/30",
};

export default function Roster() {
  const [day, setDay] = useState(nzToday());
  const [crew, setCrew] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadedAt, setLoadedAt] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError("");
    try {
      const [crewRes, shiftRes] = await Promise.all([
        API.get("/drivers"),
        API.get(`/time/shifts?from=${d}&to=${d}`),
      ]);
      setCrew(Array.isArray(crewRes.data) ? crewRes.data : []);
      setShifts(shiftRes.data?.shifts || []);
      setLoadedAt(new Date());
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(day); }, [day, load]);

  // One row per person. Crew with no shift that day still get a row, so an
  // absent driver shows as "no hours recorded" rather than vanishing - the
  // office needs to see who did NOT work as much as who did.
  const rows = useMemo(() => {
    const byPerson = new Map();
    for (const s of shifts) {
      const list = byPerson.get(s.person_id) || [];
      list.push(s);
      byPerson.set(s.person_id, list);
    }
    const people = crew
      .filter((c) => c.is_active !== false && c.is_active !== 0)
      .map((c) => ({ id: c.id, name: c.name, role: c.role || "driver" }));
    // shifts for someone no longer on the crew list still belong on the record
    for (const [pid, list] of byPerson) {
      if (!people.find((p) => p.id === pid)) {
        people.push({ id: pid, name: list[0].person_name || pid, role: list[0].role || "driver" });
      }
    }
    return people
      .map((p) => {
        const mine = (byPerson.get(p.id) || []).slice().sort((a, b) => String(a.start || "").localeCompare(String(b.start || "")));
        const paid = mine.reduce((t, s) => t + (Number(s.paid_hours) || 0), 0);
        const breaks = mine.reduce((t, s) => t + (Number(s.break_minutes) || 0), 0);
        const flags = mine.flatMap((s) => s.flags || []);
        return { ...p, shifts: mine, paid, breaks, flags };
      })
      .sort((a, b) => (a.role === b.role ? a.name.localeCompare(b.name) : a.role === "driver" ? -1 : 1));
  }, [crew, shifts]);

  const totals = useMemo(() => ({
    worked: rows.filter((r) => r.shifts.length).length,
    paid: rows.reduce((t, r) => t + r.paid, 0),
    breaks: rows.reduce((t, r) => t + r.breaks, 0),
    open: shifts.filter((s) => !s.finish).length,
    flagged: rows.filter((r) => r.flags.some((f) => f.tier === "over_limit" || f.tier === "check")).length,
  }), [rows, shifts]);

  const isToday = day === nzToday();

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Calendar size={22} className="text-blue-400" /> Daily Hours
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            What every driver and offsider worked on one day, and when they took their breaks. Breaks are recorded as start and finish times; they are paid and are not deducted.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setDay(addDay(day, -1))} className="p-2 rounded-lg bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-300" title="Previous day">
            <ChevronLeft size={16} />
          </button>
          <input
            type="date"
            value={day}
            onChange={(e) => e.target.value && setDay(e.target.value)}
            className="bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500 tnum"
          />
          <button onClick={() => setDay(addDay(day, 1))} disabled={isToday} className="p-2 rounded-lg bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-300 disabled:opacity-40" title="Next day">
            <ChevronRight size={16} />
          </button>
          {!isToday && (
            <button onClick={() => setDay(nzToday())} className="text-xs font-semibold text-blue-300 hover:text-blue-200 px-2.5 py-2 bg-blue-500/10 border border-blue-500/25 rounded-lg">
              Today
            </button>
          )}
          <button onClick={() => load(day)} disabled={loading} className="p-2 rounded-lg bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-300 disabled:opacity-50" title="Refresh">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-300">
          <AlertTriangle size={15} /> {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Worked", totals.worked, `of ${rows.length} crew`],
          ["Paid hours", fmtHours(totals.paid), "finish − start"],
          ["Breaks", fmtBreak(totals.breaks), "recorded, paid"],
          ["Still on shift", totals.open, isToday ? "no finish yet" : "never clocked out"],
          ["Flagged", totals.flagged, "over limit / check"],
        ].map(([label, value, sub]) => (
          <div key={label} className="bg-slate-900/40 border border-slate-800/80 rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="text-xl font-bold text-white tnum">{value}</div>
            <div className="text-[11px] text-slate-500">{sub}</div>
          </div>
        ))}
      </div>

      <div className="bg-slate-950/40 rounded-2xl shadow border border-slate-800/80 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <h3 className="font-bold text-white text-sm">{prettyDate(day)}</h3>
          <span className="text-[11px] text-slate-500">
            {loadedAt ? `updated ${loadedAt.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}` : ""}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/60 text-slate-400 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Person</th>
                <th className="text-left px-3 py-2.5 font-semibold">Role</th>
                <th className="text-left px-3 py-2.5 font-semibold">Start</th>
                <th className="text-left px-3 py-2.5 font-semibold">Finish</th>
                <th className="text-right px-3 py-2.5 font-semibold">Breaks</th>
                <th className="text-right px-3 py-2.5 font-semibold">Paid hrs</th>
                <th className="text-left px-3 py-2.5 font-semibold">Source</th>
                <th className="text-left px-3 py-2.5 font-semibold">Flags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {rows.length === 0 && !loading && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">No crew on record.</td></tr>
              )}
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  {r.shifts.length === 0 ? (
                    <tr className="text-slate-500">
                      <td className="px-4 py-2.5 font-semibold text-slate-300">{r.name}</td>
                      <td className="px-3 py-2.5">{ROLE_LABEL[r.role] || r.role}</td>
                      <td className="px-3 py-2.5" colSpan={6}>no hours recorded</td>
                    </tr>
                  ) : r.shifts.map((s, i) => (
                    <tr key={s.id} className={s.flags?.some((f) => f.tier === "over_limit") ? "bg-red-500/5" : s.flags?.some((f) => f.tier === "check") ? "bg-amber-500/5" : ""}>
                      <td className="px-4 py-2.5 font-semibold text-slate-100">{i === 0 ? r.name : ""}</td>
                      <td className="px-3 py-2.5 text-slate-400">{i === 0 ? (ROLE_LABEL[r.role] || r.role) : ""}</td>
                      <td className="px-3 py-2.5 text-slate-200 tnum">{s.start || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-200 tnum">
                        {s.finish || <span className="text-emerald-300 font-semibold">on shift</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-300 tnum">{fmtBreaks(s)}</td>
                      <td className="px-3 py-2.5 text-right text-white font-semibold tnum">{fmtHours(s.paid_hours)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] ${s.source === "manual" ? TIER_CLASS.info : "border-slate-700 text-slate-400"}`}>
                          {s.source === "manual" ? `keyed in${s.entered_by ? ` · ${s.entered_by}` : ""}` : s.source === "logninja" ? "Log Ninja" : "clocked"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {(s.flags || []).map((f, k) => (
                            <span key={k} title={f.message} className={`px-1.5 py-0.5 rounded border text-[10px] ${TIER_CLASS[f.tier] || TIER_CLASS.info}`}>
                              {f.code}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-slate-900/60 text-slate-200 font-semibold">
                <tr>
                  <td className="px-4 py-2.5" colSpan={4}>Day total · {totals.worked} worked</td>
                  <td className="px-3 py-2.5 text-right tnum">{fmtBreak(totals.breaks)}</td>
                  <td className="px-3 py-2.5 text-right tnum">{fmtHours(totals.paid)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <p className="text-[11px] text-slate-500">
        Pay record. Not a logbook under Land Transport Rule 62001 — the drivers' Log Ninja logbook remains the work-time record.
        Corrections and keyed-in hours are made on the Timesheets tab.
      </p>
    </div>
  );
}
